import React, { FC, useState, useEffect } from 'react';
import {
  TooltipProps,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';

interface LeaveData {
  month: string;
  paid: number;
  paid_ordinary: number;
  sick: number;
  unpaid: number;
}

interface ChartProps {
  chartData: LeaveData[];
}

const Chart: FC<ChartProps> = ({ chartData }) => {
  const { t } = useTranslation();

  const [isSmallDevice, setIsSmallDevice] = useState(window.innerWidth <= 991);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallDevice(window.innerWidth <= 991);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Define the colors for the pie chart
  const COLORS = ['#8884d8', '#32bfa3', '#2b90ef', '#ffb822'];

  // Data for the legend
  const legendData = [
    { value: t('leaves.paid'), type: 'line', color: '#8884d8' },
    { value: t('leaves.otherPaid'), type: 'line', color: '#32bfa3' },
    { value: t('leaves.unpaid'), type: 'line', color: '#2b90ef' },
    { value: t('leaves.sick'), type: 'line', color: '#ffb822' },
  ];

  // Placeholder data for the pie chart when there's no data
  const emptyPieData = [{ name: t('leaves.noLeaves'), value: 1 }];

  // Aggregate data for the pie chart
  const pieData = chartData.reduce(
    (acc, cur) => {
      acc[0].value += cur.paid_ordinary;
      acc[1].value += cur.paid;
      acc[2].value += cur.unpaid;
      acc[3].value += cur.sick;
      return acc;
    },
    [
      { name: t('leaves.paid'), value: 0 },
      { name: t('leaves.otherPaid'), value: 0 },
      { name: t('leaves.unpaid'), value: 0 },
      { name: t('leaves.sick'), value: 0 },
    ]
  );

  // Check if the pie data value is 0
  const isPieDataEmpty = pieData.every(data => data.value === 0);

  // Custom tooltip for the pie chart
  const CustomTooltip: React.FC<TooltipProps<any, any>> = ({ active, payload }) => {
    if (active && payload && payload.length && !isPieDataEmpty) {
      const { name, value } = payload[0];
      // Find the index of the data in pieData based on the name
      const dataIndex = pieData.findIndex(data => data.name === name);
      const color = COLORS[dataIndex % COLORS.length];

      return (
        <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc' }}>
          <p style={{ color, margin: 0 }}>{`${name}: ${payload[0].value}`}</p>
        </div>
      );
    }
    if (active && isPieDataEmpty) {
      return (
        <div style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc' }}>
          <p style={{ margin: 0 }}>{t('leaves.noLeaves')}</p>
        </div>
      );
    }

    return null;
  };

  const CustomPieLabel = (viewBox: any, value: any) => {
    const { cx, cy } = viewBox;
    // Position the label
    const positionY = cy > 0 ? cy - 100 : cy + 100;

    return (
      <text
        x={cx}
        y={positionY}
        fill="#ffb822"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {value}
      </text>
    );
  };

  const getLabelContent = (isSmallDeviceCurr: boolean, isPieDataEmptyCurr: boolean) => {
    if (isSmallDeviceCurr && isPieDataEmptyCurr) {
      return (props: any) => CustomPieLabel(props, t('leaves.noLeaves'));
    }
    if (isSmallDeviceCurr) {
      return (entry: { value: number; name: string }) => {
        // Only render the label if the value is greater than 0
        if (entry.value > 0) {
          return entry.name;
        }
        return null;
      };
    }
    return false;
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          maxWidth: '100%',
          margin: '0 auto',
          padding: '10px',
        }}
      >
        {legendData.map((item, index) => (
          <div
            key={index}
            style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}
          >
            <div style={{ width: 10, height: 10, backgroundColor: item.color, marginRight: 5 }} />
            <span>{item.value}</span>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: isSmallDevice ? 'column' : 'row',
          width: '100%',
          minHeight: '400px',
          height: isSmallDevice ? '80vh' : '50vh',
        }}
      >
        <ResponsiveContainer
          width={isSmallDevice ? '100%' : '70%'}
          height="100%"
        >
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          >
            <XAxis
              dataKey="month"
              padding={isSmallDevice ? { left: 10, right: 10 } : { left: 30, right: 30 }}
            />
            <YAxis
              width={isSmallDevice ? 18 : 30}
              padding={{ top: 30, bottom: 30 }}
            />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Line
              name={t('leaves.paid')}
              type="monotone"
              dataKey="paid_ordinary"
              stroke="#8884d8"
              strokeWidth={2}
              activeDot={{ r: 8 }}
            />
            <Line
              name={t('leaves.otherPaid')}
              type="monotone"
              dataKey="paid"
              stroke="#32bfa3"
              strokeWidth={2}
              activeDot={{ r: 8 }}
            />
            <Line
              name={t('leaves.unpaid')}
              type="monotone"
              dataKey="unpaid"
              stroke="#2b90ef"
              strokeWidth={2}
              activeDot={{ r: 8 }}
            />
            <Line
              name={t('leaves.sick')}
              type="monotone"
              dataKey="sick"
              stroke="#ffb822"
              strokeWidth={2}
              activeDot={{ r: 8 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <ResponsiveContainer
          width={isSmallDevice ? '100%' : '30%'}
          height="100%"
        >
          <PieChart>
            <Pie
              data={isPieDataEmpty ? emptyPieData : pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={isSmallDevice ? 80 : 120}
              label={getLabelContent(isSmallDevice, isPieDataEmpty)}
              labelLine={false}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={isPieDataEmpty ? '#ffb822' : COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

export default Chart;
